import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

const DateSelector = ({ selectedDate, onDateChange, panchangData }) => {
  // Convert date to YYYY-MM-DD for the HTML input
  const formatDateString = (date) => {
    const d = new Date(date);
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const handleInputChange = (e) => {
    if (e.target.value) {
      onDateChange(new Date(e.target.value));
    }
  };

  const shiftDay = (days) => {
    const nextDate = new Date(selectedDate);
    nextDate.setDate(selectedDate.getDate() + days);
    
    // Maintain year 2026 boundary
    if (nextDate.getFullYear() === 2026) {
      onDateChange(nextDate);
    }
  };

  const setToday2026 = () => {
    const today = new Date();
    if (today.getFullYear() === 2026) {
      onDateChange(today);
    } else {
      // Default to May 23, 2026 if current client system time is outside 2026
      onDateChange(new Date(2026, 4, 23)); 
    }
  };

  // Human readable long date
  const longDateStr = selectedDate.toLocaleDateString('hi-IN', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const englishDateStr = selectedDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="bg-white rounded-2xl border border-stone-150 p-6 shadow-sm mb-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        
        {/* Navigation & Input Controls */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Previous Day Arrow */}
          <button
            onClick={() => shiftDay(-1)}
            disabled={selectedDate.getMonth() === 0 && selectedDate.getDate() === 1}
            className="p-2.5 rounded-xl border border-stone-200 text-stone-600 hover:text-saffron-600 hover:border-saffron-300 hover:bg-saffron-50 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-stone-600 disabled:hover:border-stone-200 transition-all shadow-sm"
            title="पिछला दिन (Previous Day)"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>

          {/* Styled Calendar Input Wrapper */}
          <div className="relative flex items-center">
            <Calendar className="absolute left-3.5 h-5 w-5 text-saffron-500 pointer-events-none" />
            <input
              type="date"
              value={formatDateString(selectedDate)}
              onChange={handleInputChange}
              min="2026-01-01"
              max="2026-12-31"
              className="pl-11 pr-4 py-2.5 bg-stone-50 border border-stone-200 text-stone-850 rounded-xl focus:outline-none focus:ring-2 focus:ring-saffron-500 focus:border-saffron-500 font-medium cursor-pointer transition-all shadow-sm"
            />
          </div>

          {/* Next Day Arrow */}
          <button
            onClick={() => shiftDay(1)}
            disabled={selectedDate.getMonth() === 11 && selectedDate.getDate() === 31}
            className="p-2.5 rounded-xl border border-stone-200 text-stone-600 hover:text-saffron-600 hover:border-saffron-300 hover:bg-saffron-50 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-stone-600 disabled:hover:border-stone-200 transition-all shadow-sm"
            title="अगला दिन (Next Day)"
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          {/* Today Button */}
          <button
            onClick={setToday2026}
            className="px-4 py-2.5 rounded-xl border border-saffron-200 bg-saffron-50 text-saffron-700 hover:bg-saffron-100 hover:text-saffron-800 font-semibold text-sm transition-all shadow-sm flex items-center space-x-1.5"
          >
            <span>आज (Today)</span>
          </button>
        </div>

        {/* Selected Date Information Texts */}
        <div className="text-left md:text-right border-t md:border-t-0 pt-4 md:pt-0 border-stone-100">
          <div className="text-lg font-bold text-stone-900 leading-snug">
            {longDateStr}
          </div>
          <div className="text-sm text-stone-500 font-medium">
            {englishDateStr}
          </div>
          
          {panchangData && (
            <div className="mt-1.5 inline-flex items-center px-2.5 py-0.5 rounded-md bg-stone-100 text-stone-700 text-xs font-semibold uppercase tracking-wide">
              🕉️ {panchangData.hinduMonth.hi} • {panchangData.tithi.paksha === "Shukla" ? "शुक्ल पक्ष" : "कृष्ण पक्ष"}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default DateSelector;
